import React from 'react';
import moment from "moment";
import FlatButton from 'material-ui/FlatButton';
import Toggle from "material-ui/Toggle";
import {MdCheckbox, MdTextField} from 'core/components/fields/fields';
import Dialog from 'material-ui/Dialog';
import MdField, {DATATYPE} from 'core/metamodel/mdField';
import {FormProps, IDomainObject, isForVatRateCountry, lovLabel, lovValue} from 'core/form/formUtils';
import {FIELDDEFDISPLAYTYPE} from 'core/form/form';
import {createForm} from 'core/form/createForm';
import StyledDatePicker from 'core/components/fields/styledDatePicker';
import StyledDateInterval from "core/components/fields/styledDateInterval";
import {Validate} from 'core/form/validate';
import invoiceService, {invoiceItemGridLocation} from 'invoice/service/invoiceService';
import {ErrorComp} from 'core/components/errorComp/errorComp';
import {FIELD_HEIGHT} from "core/common/config/variables";
import {invalid, IsRequired, valid} from 'core/form/rules';
import StyledSelect from 'core/components/fields/styledSelect';
import {MenuItem as BsMenuItem} from "react-bootstrap";
import {ZzSplitButton} from "core/components/buttons/buttons";
import DocumentComp from "core/document/component/documentComp";
import DocumentService from "core/document/documentService";
import {observer} from "mobx-react";
import {canDeleteInvoiceDocument, Invoice, INVOICE_TYPE, InvoiceItem, INVOICESTATUS, INVOICEWARNING} from "invoice/domain/invoiceDomain";
import {action, observable} from "mobx";
import {COUNTRY} from "party/partyDomain";
import uiService from "core/common/service/uiService";
import {HistoryObjectField, SENDTYPE} from "core/event/eventDomain";
import {DOCUMENTSTATUS, nonReplacedDocumentFp} from "core/document/documentDomain";
import {optionsSearch} from "core/common/service/commonDataService";
import {DialogAncestor, DialogProps} from "core/common/page/dialogAncestor";
import {ClientGrid} from "core/grid/domain/grid";
import mdEntityService from "core/metamodel/mdEntityService";
import {DEFAULT_CURRENCY_FORMAT, formatNumber} from "core/common/utils/numberUtils";
import {indigo500, orange500, pink500} from "material-ui/styles/colors";
import {ENTITY_NUMBER_GEN_ENTITY} from "../constants";
import {LovItem} from "../../core/metamodel/mdEntity";
import {EmailAddressesEditor} from "core/event/component/emailComponents";
import securityService from "core/security/securityService";
import InvoiceSumTable from './invoiceSumTable';

interface InvoiceIssueDialogState {
  blockIssueBtn: boolean;
}

@observer
export default class InvoiceIssueDialog extends DialogAncestor<DialogProps, InvoiceIssueDialogState, boolean> {

  private $pristineObject: Invoice;
  @observable private emailInvoiceToggle: boolean = true;
  @observable private showSummary: boolean = false;

  private _runningOkAction: boolean = false;

  constructor(props) {
    super(props);
    this.state = {blockIssueBtn: false};
  }

  @action open(resolve, reject, invoiceTypeId: number, fastIssue: boolean): Promise<any> {
    uiService.showToolbarActionFlag();

    let {entities} = this.props;

    this.resolve = resolve;
    this.reject = reject;
    this.$errors = [];
    this.showSummary = false;

    this.$pristineObject = invoiceService.invoiceObject.cloneShallow();

    invoiceService.invoiceObject.invoiceType = entities.get('InvoiceType').getLovItem(invoiceTypeId);
    invoiceService.invoiceObject.$generateInvoiceNumber = !invoiceService.invoiceObject.invoiceNumber;

    if (invoiceService.invoiceObject.tupleMap && invoiceService.invoiceObject.tupleMap.EMAILS) invoiceService.invoiceObject.tupleMap.EMAILS = observable(invoiceService.invoiceObject.tupleMap.EMAILS);

    return invoiceService.prepareIssue(invoiceService.invoiceObject)
      .then(action("open-prepare-assue-actionCb", (invoiceJson: IDomainObject) => {
        // dokumenty nechat puvodni
        const documents = invoiceService.invoiceObject.documents;
        invoiceService.invoiceObject.fromJson(invoiceJson);
        invoiceService.invoiceObject.documents = documents;

        if (!invoiceService.invoiceObject.startInvoicePeriod) {
          invoiceService.invoiceObject.startInvoicePeriod = moment().startOf('month').format("YYYY-MM-DD");
        }
        if (!invoiceService.invoiceObject.endInvoicePeriod) {
          invoiceService.invoiceObject.endInvoicePeriod = moment().endOf('month').startOf('day').format("YYYY-MM-DD");
        }

        if (isForVatRateCountry(COUNTRY.HU)) invoiceService.invoiceObject.deliveryDate = invoiceService.invoiceObject.invoiceComputeHuDeliveryDate();

        let extendedPreferences = securityService.currentUser.extendedPreferences("Invoice");
        let enabledInvoiceWarnings = extendedPreferences ? extendedPreferences.enabledInvoiceWarnings : [];

        Object.assign(invoiceService.invoiceObject, {
          $negativeItemExists: enabledInvoiceWarnings.includes(INVOICEWARNING.negativeItemExists) && ([INVOICE_TYPE.FI, INVOICE_TYPE.PR, INVOICE_TYPE.DP, INVOICE_TYPE.AD].includes(invoiceTypeId) && invoiceService.invoiceObject.invoiceNegativeItemExists()),
          $positiveItemExists: enabledInvoiceWarnings.includes(INVOICEWARNING.positiveItemExists) && (invoiceTypeId === INVOICE_TYPE.CN && invoiceService.invoiceObject.invoicePositiveItemExists()),
          $negativeItemResolved: false,
          $positiveItemResolved: false,
          $exchangeRateResolved: (invoiceTypeId === INVOICE_TYPE.CN),
          $exchangeRateDifferent: enabledInvoiceWarnings.includes(INVOICEWARNING.exchangeRateDifferent) && (invoiceService.invoiceObject.tupleMap.exchangeRate !== invoiceService.invoiceObject.exchangeRate),
          $vatRateResolved: (invoiceTypeId === INVOICE_TYPE.CN),
          $vatRateDifferent: enabledInvoiceWarnings.includes(INVOICEWARNING.differentVatRates) && (invoiceService.invoiceObject.tupleMap.differentVatRates && invoiceService.invoiceObject.tupleMap.differentVatRates.length),
          $vehicleOwners: enabledInvoiceWarnings.includes(INVOICEWARNING.vehicleOwners) && ((invoiceService.invoiceObject.tupleMap.vehicleOwners) ? invoiceService.invoiceObject.tupleMap.vehicleOwners.filter(t => lovValue(t.value1) != lovValue(invoiceService.invoiceObject.billedTo)) : []),
          $differentFields: enabledInvoiceWarnings.includes(INVOICEWARNING.differentFields) && this.filterDifferentFields(invoiceService.invoiceObject.tupleMap.differentFields)
        });

        uiService.hideToolbarActionFlag();

        let thruDialog = !fastIssue || this.disableIssue(invoiceService.invoiceObject);

        if (thruDialog) {
          this.dialogOpen = true;
        } else {
          let sendType = lovValue(invoiceService.invoiceObject.sendType);
          return this.ok(sendType, thruDialog);
        }

      }));
  }

  /**
   * If a customer does not have set its default profit centre we cannot take it as a new value
   */
  private filterDifferentFields(differentFields: HistoryObjectField[]) {
    return differentFields.filter(hof => {
      if (hof.fieldName === 'profitCenter')
        return !!hof.newValue;

      return true;
    })
  }

  @action ok = (send: SENDTYPE, thruDialog: boolean) => {
    console.log('Issuing invoice, send = ' + send);

    if (this._runningOkAction) return;

    let form = invoiceService.invoiceObject.getForm('InvoiceIssueForm');
    let res = form.validateForm(invoiceService.invoiceObject);

    if (!res) {
      return;
    }

    this._runningOkAction = true;
    uiService.showToolbarActionFlag();

    if (!invoiceService.invoiceObject.tupleMap) invoiceService.invoiceObject.tupleMap = {};
    invoiceService.invoiceObject.tupleMap.SEND = send;

    let invoicePrintWindow;

    // remove invoice period if it was disabled
    if (!invoiceService.invoiceObject.periodicInvoice) {
      invoiceService.invoiceObject.startInvoicePeriod = null;
      invoiceService.invoiceObject.endInvoicePeriod = null;
    }

    invoiceService.doIssue(invoiceService.invoiceObject)
      .then(action("ok-doIssue-actionCb", (issuedInvoiceJson: IDomainObject) => {
        const wasNew: boolean = invoiceService.invoiceObject.cleanAfterSave(issuedInvoiceJson, false);
        this.dialogOpen = false;
        this.$pristineObject = undefined;
        this.resolve(wasNew);

        this._runningOkAction = false;

        // otevru v novem okne
        if (send === SENDTYPE.POST) {
          let maxDocumentById = invoiceService.invoiceObject.documents.filter(doc => doc.status === DOCUMENTSTATUS.VALID)
            .reduce((prev, next) => (prev.documentId > next.documentId) ? prev : next )
          DocumentService.printDocument(maxDocumentById.documentUuid);
        }
      }), error => {
        this._runningOkAction = false;
        this.$errors = error.getMessages();
      });


  };


  @action cancel = (evt) => {
    console.log('InvoiceIssueDialog#cancel evt = %O', evt);
    this.dialogOpen = false;
    invoiceService.invoiceObject.copyStandardFields(this.$pristineObject);
    this.$pristineObject = undefined;
    this.$errors = undefined;
    //invoiceObject.invoiceNumber = undefined;
    let grid = invoiceService.invoiceObject.$movingBlocks.getBlock(invoiceItemGridLocation) as ClientGrid<InvoiceItem>;
    grid.generateGridData(invoiceService.invoiceObject);

    this.reject("cancel");
  };

  @action cancelKeepChanges = () => {
    invoiceService.invoiceObject.$dirty = true;
    this.dialogOpen = false;
    this.$pristineObject = undefined;
    this.$errors = undefined;
    //invoiceObject.invoiceNumber = undefined;
    let grid = invoiceService.invoiceObject.$movingBlocks.getBlock(invoiceItemGridLocation) as ClientGrid<InvoiceItem>;
    grid.generateGridData(invoiceService.invoiceObject);

    this.reject("cancel");
  };


  @action toNegative = (doResolve) => {
    let promise: Promise<any> = (doResolve) ? invoiceService.recalculateWithClean(invoiceService.invoiceObject, {toNegative: true}) : Promise.resolve(true);
    promise.then(() => {
      invoiceService.invoiceObject.$positiveItemResolved = true;
      this.showSummary = true;
    });
  };


  @action toPositive = (doResolve) => {
    let promise: Promise<any> = (doResolve) ? invoiceService.recalculateWithClean(invoiceService.invoiceObject, {toPositive: true}) : Promise.resolve(true);
    promise.then(() => {
      invoiceService.invoiceObject.$negativeItemResolved = true;
      this.showSummary = true;
    });
  };


  @action changeExchangeRate = (doResolve) => {
    if (doResolve) {
      invoiceService.invoiceObject.exchangeRate = invoiceService.invoiceObject.tupleMap.exchangeRate;
    }
    let promise: Promise<any> = (doResolve) ? invoiceService.recalculateWithClean(invoiceService.invoiceObject, {}) : Promise.resolve(true);
    promise.then(() => {
      invoiceService.invoiceObject.$exchangeRateResolved = true;
      this.showSummary = true;
    });
  };


  @action changeVatRate = (doResolve) => {
    let promise: Promise<any> = (doResolve) ? invoiceService.recalculateWithClean(invoiceService.invoiceObject, {fixVatRate: true}) : Promise.resolve(true);
    promise.then(() => {
      invoiceService.invoiceObject.$vatRateResolved = true;
      this.showSummary = true;
    });
  };

  @action resolveDifferentValue = (fieldName: string, newValue: any) => {
    invoiceService.invoiceObject[fieldName] = newValue;
    invoiceService.invoiceObject.$differentFields.splice(invoiceService.invoiceObject.$differentFields.findIndex(hof => hof.fieldName === fieldName), 1);
  };

  @action resolveDifferentInvoice = () => {
    invoiceService.invoiceObject.$vehicleOwnersResolved = true;
  };

  @action showSendToggleHandler = (evt, bulik) => {
    this.emailInvoiceToggle = bulik;
  };


  changeBlockIssueBtn = (block: boolean) => {
    this.setState({
      blockIssueBtn: block,
    });
  }

  disableIssue(invoiceObject: Invoice) {
    return (!invoiceObject.$negativeItemResolved && invoiceObject.$negativeItemExists)
      || (!invoiceObject.$positiveItemResolved && invoiceObject.$positiveItemExists)
      || (!invoiceObject.$exchangeRateResolved && invoiceObject.$exchangeRateDifferent)
      || (!invoiceObject.$vatRateResolved && invoiceObject.$vatRateDifferent)
      || (invoiceObject.tupleMap.invalidVatRates && invoiceObject.tupleMap.invalidVatRates.length)
      || (!invoiceObject.$vehicleOwnersResolved && invoiceObject.$vehicleOwners.length)
      || invoiceObject.$differentFields.length
      || (invoiceObject.$generateInvoiceNumber && !invoiceObject.entityNumberGen && !invoiceObject.tupleMap.hasEntityNumberGenDefault)
      || this.state.blockIssueBtn

      ;
  }


  render() {

    let {entities} = this.props;

    if (!(invoiceService.invoiceObject && this.dialogOpen)) return null;

    let invoiceObject = invoiceService.invoiceObject;
    let invoiceEntity = mdEntityService.getEntity("Invoice");
    let tupleMap = invoiceService.invoiceObject.tupleMap;

    let sendType = lovValue(invoiceObject.sendType);

    let disableIssue = this.disableIssue(invoiceObject);

    let finalInvoiceRels = invoiceObject.getFinalInvoiceRels();


    console.debug('this.state %O, disableIssue = %s', this.state, disableIssue);

    const actions = [

      (sendType === SENDTYPE.POST) ?
        <ZzSplitButton key={1} title="Issue" onClick={this.ok.bind(this, null, true)} secondary disabled={disableIssue} id="issue-button">
          <BsMenuItem eventKey={0} onSelect={this.ok.bind(this, null, true)}>Issue</BsMenuItem>
          <BsMenuItem eventKey={1} onSelect={this.ok.bind(this, sendType, true)}>Issue and print</BsMenuItem>
        </ZzSplitButton>
        :
        <FlatButton key={1}
                    label="Issue"
                    primary
                    disabled={disableIssue}
                    onClick={this.ok.bind(this, (this.emailInvoiceToggle) ? sendType : null, true)}
        />,
      <FlatButton key={2}
                  label="Cancel"
                  primary
                  onClick={this.cancel}
      />,
      <FlatButton key={3}
                  label="Cancel & keep changes"
                  primary
                  onClick={this.cancelKeepChanges}
      />
    ];


    return (
      <Dialog
        title={`Issue  ${lovLabel(invoiceObject.invoiceType)}`}
        actions={actions}
        modal={true}
        open={this.dialogOpen}
        autoScrollBodyContent={true}
        contentStyle={{width: '80%', maxWidth: 800}}

      >

        <ErrorComp messages={this.$errors}/>


        {(lovValue(invoiceObject.invoiceType) === INVOICE_TYPE.CN && !finalInvoiceRels.length) ? (
          <div style={{display: 'flex'}}>
            <h5 style={{marginRight: 2, color: orange500, marginTop: 12}}>This Credit Note is not linked to Final Invoice</h5>
          </div>
        ) : null}


        {(invoiceObject.$generateInvoiceNumber && !invoiceObject.entityNumberGen && !invoiceObject.tupleMap.hasEntityNumberGenDefault) ? (
          <div style={{display: 'flex'}}>
            <h5 style={{marginRight: 2, color: orange500, marginTop: 12}}>There is no default Invoice Number, choose it, please</h5>
          </div>
        ) : null}

        {(invoiceObject.$generateInvoiceNumber && invoiceObject.tupleMap.hasSeveralDefaultEntityNumberGen) ? (
          <div style={{display: 'flex'}}>
            <h5 style={{marginRight: 2, color: orange500, marginTop: 12}}>There is several default Invoice Numbers, choose one, please</h5>
          </div>
        ) : null}

        {(!invoiceObject.$negativeItemResolved && invoiceObject.$negativeItemExists) ? (
          <div style={{display: 'flex'}}>
            <h5 style={{marginRight: 2, color: pink500, marginTop: 12}}>There are items with negative quantity or
              price, </h5>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="revert them" primary
                        onClick={this.toPositive.bind(this, true)}/>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="keep them" primary
                        onClick={this.toPositive.bind(this, false)}/>
          </div>
        ) : null}

        {(!invoiceObject.$positiveItemResolved && invoiceObject.$positiveItemExists) ? (
          <div style={{display: 'flex'}}>
            <h5 style={{marginRight: 2, color: pink500, marginTop: 12}}>There are items with positive quantity or
              price, </h5>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="revert them" primary
                        onClick={this.toNegative.bind(this, true)}/>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="keep them" primary
                        onClick={this.toNegative.bind(this, false)}/>
          </div>
        ) : null}

        {(!invoiceObject.$exchangeRateResolved && invoiceObject.$exchangeRateDifferent) ? (
          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            <h5 style={{
              marginRight: 2,
              color: pink500,
              marginTop: 12
            }}>{`Copied exchange rate ${invoiceObject.exchangeRate} differs from current rate ${invoiceObject.tupleMap.exchangeRate}`} </h5>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Use current" primary
                        onClick={this.changeExchangeRate.bind(this, true)}/>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Keep existing" primary
                        onClick={this.changeExchangeRate.bind(this, false)}/>
          </div>
        ) : null}

        {(invoiceObject.tupleMap.invalidVatRates && invoiceObject.tupleMap.invalidVatRates.length) ? (
          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            <h5 style={{
              marginRight: 2,
              color: pink500,
              marginTop: 12
            }}>{`Following VAT Rates are not valid for selected Tax Date: ${invoiceObject.tupleMap.invalidVatRates.join(', ')}`} </h5>
          </div>
        ) : null}

        {(!invoiceObject.$vatRateResolved && invoiceObject.$vatRateDifferent) ? (
          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            <h5 style={{
              marginRight: 2,
              color: pink500,
              marginTop: 12
            }}>{invoiceObject.tupleMap.differentVatRates.join(', ')} </h5>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Use correct" primary
                        onClick={this.changeVatRate.bind(this, true)}/>
            <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Keep existing" primary
                        onClick={this.changeVatRate.bind(this, false)}/>
          </div>
        ) : null}

        {(!invoiceObject.$vehicleOwnersResolved && invoiceObject.$vehicleOwners.length) ? (
          <div>
            {
              invoiceObject.$vehicleOwners.map(tuple => (
                <div style={{display: 'flex', flexWrap: 'wrap'}}>
                  <h5 style={{marginRight: 2, color: pink500, marginTop: 12}}>
                    Vehicles {tuple.value2.map(lov => lovLabel(lov)).join(", ")} have owner {lovLabel(tuple.value1)} who
                    differs from invoice customer
                  </h5>
                  <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Issue anyways" primary
                              onClick={this.resolveDifferentInvoice}/>
                </div>
              ))
            }
          </div>
        ) : null}


        {(invoiceObject.$differentFields.length) ? (
          <div>
            {invoiceObject.$differentFields.map((hof, index) => {
              let mdField = invoiceEntity.getField(hof.fieldName);
              return (
                <div key={index} style={{display: 'flex', flexWrap: 'wrap'}}>
                  <h5 style={{marginRight: 2, color: pink500, marginTop: 12}}>
                    Field {mdField.formLabelActive} has outdated value {mdField.formatValue(hof.oldValue)}. New value
                    is {mdField.formatValue(hof.newValue)}
                  </h5>
                  <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Use new" primary
                              onClick={this.resolveDifferentValue.bind(this, hof.fieldName, hof.newValue)}/>
                  <FlatButton labelStyle={{fontSize: 16}} style={{height: 48}} label="Keep old" primary
                              onClick={this.resolveDifferentValue.bind(this, hof.fieldName, hof.oldValue)}/>
                </div>
              )
            })}
          </div>
        ) : null}


        {(invoiceObject.cnOutstanding > 0 && lovValue(invoiceObject.invoiceType) === INVOICE_TYPE.CN) ? (
          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            <h5 style={{
              marginRight: 2,
              color: indigo500,
              marginTop: 12
            }}>{`Credit Note Amount is higher than Invoice's unsettled amount. ${formatNumber(invoiceObject.cnOutstanding, DEFAULT_CURRENCY_FORMAT)} will be added to Customer's credit.`}</h5>
          </div>
        ) : null}

        <InvoiceIssueForm entities={entities} entity={entities.get('Invoice')} changeBlockIssueBtn={this.changeBlockIssueBtn} dataObject={invoiceObject}
                          rootObject={invoiceObject}/>


        <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px 10px'}}>
          <DocumentComp documents={invoiceObject.documents} entities={entities} style={{flex: '1 1 auto'}}
                        showFilterFn={nonReplacedDocumentFp}
                        enableUpload={invoiceObject.invoiceStatus === INVOICESTATUS.CONCEPT}
                        rootObject={invoiceObject} documentEntity={entities.get('Document')}
                        canDeleteFn={canDeleteInvoiceDocument}/>
          {this.showSummary && <InvoiceSumTable invoiceObject={invoiceObject} entities={mdEntityService.entityMap}
                           invoiceEntity={invoiceEntity} minimal={true}
                           style={{minWidth: '50%'}}/>}
        </div>

        {(sendType === SENDTYPE.EMAIL) &&
          <Toggle label="Email invoice to customer" toggled={this.emailInvoiceToggle}
                  onToggle={this.showSendToggleHandler} style={{width: "unset", marginTop: 20}}
                  labelStyle={{marginRight: 10, whiteSpace: 'nowrap'}}/>
        }
        {(sendType === SENDTYPE.EMAIL) && (this.emailInvoiceToggle) && tupleMap && tupleMap.EMAILS && <EmailAddressesEditor emails={tupleMap.EMAILS}/>}




      </Dialog>
    );
  }


}


interface InvoiceIssueFormState {
  servicePeriod: string;
  defaultEntityNumberGen?: LovItem;
  showNumberGenField: boolean;
  optionsForNumberGen: LovItem[];
}

interface InvoiceIssueFormProps extends FormProps<Invoice, Invoice> {
  changeBlockIssueBtn?: (block: boolean) => void;
}

@createForm({
  formName: 'InvoiceIssueForm',
  fields: [{
    name: 'invoiceDate',
    validators: ['IsRequired']
  }, {
    name: 'taxDate',
    validators: ['IsRequired']
  }, {
    name: 'dueDate',
    validators: ['IsRequired']
  }, {
    name: 'invoiceNumber'
  }, {
    name: 'deliveryDate'
  }, {
    name: 'startInvoicePeriod'
  }, {
    name: 'endInvoicePeriod'
  }, {
    name: 'entityNumberGen'
  }, {
    name: 'periodicInvoice'
  }, {
    name: '$generateInvoiceNumber',
    displayType: FIELDDEFDISPLAYTYPE.checkbox,
    mdField: new MdField({label: 'Generate Invoice Number', dataType: DATATYPE.BOOLEAN})
  }]
}, () => <noscript/>)
@observer
class InvoiceIssueForm extends React.Component<InvoiceIssueFormProps, InvoiceIssueFormState> {

  private oldInvoiceNumber: string;

  state: InvoiceIssueFormState = {servicePeriod: 'thisMonth', defaultEntityNumberGen: null, showNumberGenField: false, optionsForNumberGen: []};

  componentWillMount() {
    let {dataObject, form} = this.props;
    const showNumberGenField: boolean = this.updateShowNumberGenFieldValue(dataObject);
    if (showNumberGenField) {
      optionsSearch(ENTITY_NUMBER_GEN_ENTITY, this.getNumberGenLoadOptionParams(), '*')
        .then(res => {
          const firstNumberGen = dataObject.tupleMap.hasEntityNumberGenDefault === false ? null : res.options[0]


          dataObject.entityNumberGen = firstNumberGen
          this.setState({
            defaultEntityNumberGen: firstNumberGen,
            optionsForNumberGen: res.options
          });

          form.updateFieldsPropValue(dataObject, "entityNumberGen");
        });
    }
  }

  @action onSuccessInvoiceDate = () => {
    let {dataObject, rootObject, entities, form} = this.props;

    // dueDateDays

    console.log('onChangeInvoiceDate ', dataObject.invoiceDate);

    this.changeBlockIssueBtn(true);

    let prepareIssuePromise = Promise.resolve(null);

    if (dataObject.invoiceDate) {
      dataObject.taxDate = dataObject.invoiceDate;

      if (isForVatRateCountry(COUNTRY.HU)) dataObject.deliveryDate = dataObject.invoiceComputeHuDeliveryDate();

      if (this.state.showNumberGenField) dataObject.entityNumberGen = null;
      prepareIssuePromise = this.prepareIssue()
        .then(() => {
          const showNumberGenField: boolean = this.updateShowNumberGenFieldValue(dataObject);
          if (showNumberGenField) {
            optionsSearch(ENTITY_NUMBER_GEN_ENTITY, this.getNumberGenLoadOptionParams(), '*')
              .then(res => {
                const firstNumberGen = dataObject.tupleMap.hasEntityNumberGenDefault === false ? null : res.options[0]

                if (dataObject.$generateInvoiceNumber) dataObject.entityNumberGen = firstNumberGen;
                this.setState({
                  defaultEntityNumberGen: firstNumberGen,
                  optionsForNumberGen: res.options
                });

                form.updateFieldsPropValue(dataObject, "entityNumberGen");
              })
          }
        });
    }

    return prepareIssuePromise.then(action("prepareIssuePromise-actionCb", () => {
      rootObject.$dirty = true;

      form.updateAllFieldsPropValue(dataObject);

      this.changeBlockIssueBtn(false);
    }));
  };

  private updateShowNumberGenFieldValue(dataObject: Invoice): boolean {
    const showNumberGenField: boolean = !dataObject.entityNumberGen && (!dataObject.tupleMap.hasEntityNumberGenDefault || dataObject.tupleMap.hasSeveralDefaultEntityNumberGen);
    this.setState({
      showNumberGenField: showNumberGenField
    });

    return showNumberGenField;
  }


  @action onSuccessTaxDate = () => {
    let {dataObject, rootObject, entities, form} = this.props;

    console.log('onChangeTaxDate ', dataObject.taxDate);

    let prepareIssuePromise = Promise.resolve(null);

    if (dataObject.taxDate) {
      if ([INVOICE_TYPE.FI, INVOICE_TYPE.PR, INVOICE_TYPE.DP].includes(lovValue(dataObject.invoiceType))) {
        prepareIssuePromise = this.prepareIssue();
      }
    }

    return prepareIssuePromise.then(action("prepareIssuePromise-actionCb", () => {
      rootObject.$dirty = true;
    }));
  };



  @action prepareIssue() {
    let {dataObject, rootObject} = this.props;

    return invoiceService.prepareIssue(dataObject)
      .then(action("prepareIssue-actionCb", (newInvoiceJson: IDomainObject) => {
        const newInvoice = new Invoice(newInvoiceJson);
        dataObject.tupleMap.exchangeRate = newInvoice.tupleMap.exchangeRate;
        dataObject.tupleMap.differentVatRates = newInvoice.tupleMap.differentVatRates;
        dataObject.tupleMap.invalidVatRates = newInvoice.tupleMap.invalidVatRates;
        dataObject.cnOutstanding = newInvoice.cnOutstanding;
        dataObject.tupleMap.hasEntityNumberGenDefault = newInvoice.tupleMap.hasEntityNumberGenDefault;
        dataObject.tupleMap.hasSeveralDefaultEntityNumberGen = newInvoice.tupleMap.hasSeveralDefaultEntityNumberGen;
        dataObject.$exchangeRateDifferent = (dataObject.tupleMap.exchangeRate !== dataObject.exchangeRate);
        dataObject.$exchangeRateResolved = (lovValue(dataObject.invoiceType) === INVOICE_TYPE.CN);
        dataObject.$vatRateDifferent = (dataObject.tupleMap.differentVatRates && dataObject.tupleMap.differentVatRates.length);
        dataObject.dueDate = newInvoice.dueDate;
      }));
  }

  @action onGenerateInvoiceNumberCheck = (evt) => {
    let {dataObject, form} = this.props;
    form.getField("$generateInvoiceNumber").props.onCheck(evt); //changing state of $generateInvoiceNumber

    console.log("onGenerateInvoiceNumberCheck dataObject.$generateInvoiceNumber = " +  dataObject.$generateInvoiceNumber);

    if (dataObject.invoiceNumber) this.oldInvoiceNumber = dataObject.invoiceNumber;

    dataObject.invoiceNumber = (dataObject.$generateInvoiceNumber) ? null : this.oldInvoiceNumber;
    form.getField("invoiceNumber").valid = true;

    if (this.state.showNumberGenField) {
      dataObject.entityNumberGen = dataObject.$generateInvoiceNumber ? this.state.defaultEntityNumberGen : null;
      form.getField("entityNumberGen").valid = true;
    }

    form.updateFieldsPropValue(dataObject, "invoiceNumber", "entityNumberGen");
  };

  getNumberGenLoadOptionParams = () => {
    let {dataObject} = this.props;

    return dataObject.tupleMap.hasEntityNumberGenDefault ?
      {
        ...dataObject.getInvoiceParamsForNumberGenSearch(),
        partyId: dataObject.billedTo.value,
        invoiceTypeId: dataObject.invoiceType.value,
        profitCenterId: dataObject.profitCenter.value,
      } :
      dataObject.getInvoiceParamsForNumberGenSearch();
  };

  changeBlockIssueBtn = (block: boolean) => {
    this.props.changeBlockIssueBtn(block)
  }

  render() {
    let {dataObject, rootObject, entities, index, formName} = this.props;

    let {
      invoiceNumber,
      invoiceDate,
      taxDate,
      dueDate,
      deliveryDate,
      $generateInvoiceNumber,
      startInvoicePeriod,
      endInvoicePeriod,
      periodicInvoice,
      entityNumberGen
    }
      = dataObject.getFormFields(formName);

    let invoiceNumberStyle = (dataObject.$generateInvoiceNumber) ? {visibility: 'hidden'} : {};

    let finalInvoiceRels = dataObject.getFinalInvoiceRels();

    return (
      <form>

        <div className="row">
          <div className="col-xs-12 col-sm-6">

            <div className="row">
              <div className="col-xs-11">
                <MdCheckbox {...$generateInvoiceNumber.props} onCheck={this.onGenerateInvoiceNumberCheck}/>
              </div>
            </div>

            <div className="row">
              {(dataObject.$generateInvoiceNumber) ? (
                (this.state.showNumberGenField) ? (
                  <div className="col-xs-12 col-sm-6">
                    <StyledSelect  {...entityNumberGen.props}
                      // loadOptions={optionsSearch.bind(this, ENTITY_NUMBER_GEN_ENTITY, this.getNumberGenLoadOptionParams())}
                                   options={this.state.optionsForNumberGen}
                                   clearable={false}
                    />
                    <Validate field={entityNumberGen} value={entityNumberGen.props.value}>
                      <IsRequired/>
                    </Validate>
                  </div>
                ) : (
                  <div className="col-xs-12 col-sm-6" style={{height: FIELD_HEIGHT}}/>
                )
              ) : (
                <div className="col-xs-12 col-sm-6" style={invoiceNumberStyle}>
                  <MdTextField {...invoiceNumber.props}  />
                  <Validate field={invoiceNumber} value={invoiceNumber.props.value}>
                    <IsRequired/>
                  </Validate>
                </div>
              )
              }

              {(lovValue(dataObject.invoiceType) === INVOICE_TYPE.CN) ? (
                <div className="col-xs-12 col-sm-6">
                  { finalInvoiceRels.map(ir => (
                    <div>{lovLabel(ir.toInvoice)}</div>
                  )) }
                </div>
              ) : null}

            </div>

            <div className="row">
              <div className="col-xs-11">
                <MdCheckbox {...periodicInvoice.props} style={{height: 30, marginTop: 10}}/>
              </div>
            </div>

            {dataObject.periodicInvoice &&
              <div className="row">
                <div className="col-xs-10">
                  <StyledDateInterval {...startInvoicePeriod.props} valueFrom={startInvoicePeriod.props.value}
                                      valueTo={endInvoicePeriod.props.value}
                                      onChangeFrom={startInvoicePeriod.props.onChange}
                                      onChangeTo={endInvoicePeriod.props.onChange}/>
                </div>
              </div>
            }


          </div>

          <div className="col-xs-12 col-sm-6">

            <div className="row">
              <div className="col-xs-10 col-sm-5 col-sm-offset-1">
                <StyledDatePicker {...invoiceDate.props}  />
                <Validate field={invoiceDate} value={invoiceDate.props.value} onSuccess={this.onSuccessInvoiceDate}/>
              </div>
              <div className="col-xs-10 col-sm-5 col-sm-offset-1">
                <StyledDatePicker {...deliveryDate.props}  />
              </div>
            </div>
            <div className="row">
              <div className="col-xs-10 col-sm-5 col-sm-offset-1">
                <StyledDatePicker {...taxDate.props} />
                <Validate field={taxDate} value={taxDate.props.value} onSuccess={this.onSuccessTaxDate}/>
              </div>
              <div className="col-xs-10 col-sm-5 col-sm-offset-1">
                <StyledDatePicker {...dueDate.props}  />
                <Validate field={dueDate} value={dueDate.props.value}/>
              </div>
            </div>

          </div>

        </div>


      </form>


    );
  }
}




// WEBPACK FOOTER //
// ./src/invoice/component/invoiceIssueDialog.tsx